// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * Agora Service
 *
 * Wrapper service for the Agora RTC SDK that handles video/voice calls.
 * Manages audio/video tracks, channel connections, and call state.
 */

import AgoraRTC, {
  type IAgoraRTCClient,
  type IMicrophoneAudioTrack,
  type ICameraVideoTrack,
  type IAgoraRTCRemoteUser,
  type IRemoteAudioTrack,
  type IRemoteVideoTrack,
  type UID,
} from 'agora-rtc-sdk-ng';

// ============================================
// Types
// ============================================

export type CallType = 'voice-call' | 'video-call';

export interface AgoraCallOptions {
  /** Agora App ID */
  appId: string;
  /** Channel name to join */
  channelName: string;
  /** RTC token from server */
  token: string;
  /** User ID for this participant */
  uid: string | number;
  /** Call type (voice or video) */
  callType: CallType;
  /** Container element ID for local video */
  localVideoContainerId?: string;
  /** Container element ID for remote video */
  remoteVideoContainerId?: string;
}

export interface AgoraCallState {
  /** Whether connected to channel */
  isConnected: boolean;
  /** Whether local audio is muted */
  isMuted: boolean;
  /** Whether local camera is off */
  isCameraOff: boolean;
  /** Whether screen sharing is active */
  isScreenSharing: boolean;
  /** Remote user info */
  remoteUser: {
    uid: UID;
    hasAudio: boolean;
    hasVideo: boolean;
  } | null;
}

export interface AgoraServiceCallbacks {
  /** Called when connection state changes */
  onConnectionStateChange?: (state: 'connecting' | 'connected' | 'disconnected' | 'failed') => void;
  /** Called when remote user joins */
  onUserJoined?: (uid: UID) => void;
  /** Called when remote user leaves */
  onUserLeft?: (uid: UID, reason: string) => void;
  /** Called when remote user publishes audio/video */
  onUserPublished?: (uid: UID, mediaType: 'audio' | 'video') => void;
  /** Called when remote user unpublishes audio/video */
  onUserUnpublished?: (uid: UID, mediaType: 'audio' | 'video') => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
}

// ============================================
// Agora Service Class
// ============================================

export class AgoraService {
  private client: IAgoraRTCClient | null = null;
  private localAudioTrack: IMicrophoneAudioTrack | null = null;
  private localVideoTrack: ICameraVideoTrack | null = null;
  private remoteAudioTrack: IRemoteAudioTrack | null = null;
  private remoteVideoTrack: IRemoteVideoTrack | null = null;
  private callbacks: AgoraServiceCallbacks = {};
  private state: AgoraCallState = {
    isConnected: false,
    isMuted: false,
    isCameraOff: false,
    isScreenSharing: false,
    remoteUser: null,
  };

  constructor() {
    // Set log level in development
    if (import.meta.env.DEV) {
      AgoraRTC.setLogLevel(1); // INFO level
    } else {
      AgoraRTC.setLogLevel(3); // WARN level
    }
  }

  /**
   * Initialize and join a call
   */
  async joinCall(options: AgoraCallOptions, callbacks: AgoraServiceCallbacks = {}): Promise<void> {
    this.callbacks = callbacks;

    try {
      // Create RTC client
      this.client = AgoraRTC.createClient({
        mode: 'rtc',
        codec: 'vp8',
      });

      // Set up event handlers
      this.setupEventHandlers();

      // Notify connecting state
      this.callbacks.onConnectionStateChange?.('connecting');

      // Check device availability before joining
      const deviceCheck = await this.checkAndRequestDevicePermissions(options.callType);
      if (!deviceCheck.audio) {
        throw new Error(
          'Microphone not available. Please check that a microphone is connected and you have granted permission to use it.'
        );
      }
      if (options.callType === 'video-call' && !deviceCheck.video) {
        console.warn('Camera not available, proceeding with audio only');
      }

      // Join the channel
      await this.client.join(
        options.appId,
        options.channelName,
        options.token,
        options.uid
      );

      // Create local tracks based on call type
      if (options.callType === 'video-call') {
        // Try to create both audio and video tracks for video calls
        try {
          if (deviceCheck.video) {
            [this.localAudioTrack, this.localVideoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
          } else {
            // Fallback to audio only if no camera
            this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
            this.state.isCameraOff = true;
          }
        } catch (trackError: unknown) {
          // If camera fails, try audio only
          console.warn('Failed to create camera track, falling back to audio only:', trackError);
          this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          this.state.isCameraOff = true;
        }

        // Play local video in container if specified
        if (options.localVideoContainerId && this.localVideoTrack) {
          this.localVideoTrack.play(options.localVideoContainerId);
        }
      } else {
        // Create only audio track for voice calls
        this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        this.state.isCameraOff = true;
      }

      // Publish local tracks
      const tracksToPublish = [
        this.localAudioTrack,
        this.localVideoTrack,
      ].filter(Boolean) as (IMicrophoneAudioTrack | ICameraVideoTrack)[];

      if (tracksToPublish.length > 0) {
        await this.client.publish(tracksToPublish);
      }

      // Update state
      this.state.isConnected = true;
      this.callbacks.onConnectionStateChange?.('connected');

    } catch (error: unknown) {
      console.error('Failed to join call:', error);
      this.callbacks.onConnectionStateChange?.('failed');

      // Provide user-friendly error messages
      const errorMessage = this.getReadableErrorMessage(error);
      this.callbacks.onError?.(new Error(errorMessage));
      throw new Error(errorMessage);
    }
  }

  /**
   * Check and request device permissions, with graceful fallback
   */
  private async checkAndRequestDevicePermissions(callType: CallType): Promise<{
    audio: boolean;
    video: boolean;
  }> {
    try {
      // First, try to enumerate devices to see what's available
      const devices = await AgoraRTC.getDevices();
      const hasAudioInput = devices.some((d) => d.kind === 'audioinput' && d.deviceId !== '');
      const hasVideoInput = devices.some((d) => d.kind === 'videoinput' && d.deviceId !== '');

      // If devices are listed without labels, we may need to request permission first
      const needsPermissionRequest = devices.some((d) => d.label === '');

      if (needsPermissionRequest) {
        // Request permissions by briefly creating and stopping tracks
        try {
          const constraints: MediaStreamConstraints = {
            audio: true,
            video: callType === 'video-call',
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          // Stop all tracks immediately after getting permission
          stream.getTracks().forEach((track) => track.stop());

          // Re-enumerate devices after permission granted
          const devicesAfterPermission = await AgoraRTC.getDevices();
          return {
            audio: devicesAfterPermission.some((d) => d.kind === 'audioinput' && d.deviceId !== ''),
            video: devicesAfterPermission.some((d) => d.kind === 'videoinput' && d.deviceId !== ''),
          };
        } catch (permError) {
          console.warn('Permission request failed:', permError);
          // Return what we know from initial enumeration
          return { audio: hasAudioInput, video: hasVideoInput };
        }
      }

      return { audio: hasAudioInput, video: hasVideoInput };
    } catch {
      // If device enumeration fails entirely, try to proceed anyway
      console.warn('Device enumeration failed, attempting to proceed');
      return { audio: true, video: callType === 'video-call' };
    }
  }

  /**
   * Convert Agora/WebRTC errors to user-friendly messages
   */
  private getReadableErrorMessage(error: unknown): string {
    const errorStr = String(error);
    const errorMessage = error instanceof Error ? error.message : errorStr;

    // Device not found errors
    if (errorStr.includes('DEVICE_NOT_FOUND') || errorStr.includes('NotFoundError')) {
      if (errorStr.includes('video') || errorStr.includes('camera')) {
        return 'Camera not found. Please connect a camera or check your browser permissions.';
      }
      if (errorStr.includes('audio') || errorStr.includes('microphone')) {
        return 'Microphone not found. Please connect a microphone or check your browser permissions.';
      }
      return 'Audio/video device not found. Please check that your camera and microphone are connected.';
    }

    // Permission denied errors
    if (errorStr.includes('NotAllowedError') || errorStr.includes('PERMISSION_DENIED')) {
      return 'Permission denied. Please allow access to your camera and microphone in your browser settings.';
    }

    // Device in use errors
    if (errorStr.includes('NotReadableError') || errorStr.includes('DEVICE_IN_USE')) {
      return 'Device is in use by another application. Please close other apps using your camera or microphone.';
    }

    // Network errors
    if (errorStr.includes('NETWORK') || errorStr.includes('NetworkError')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Token errors
    if (errorStr.includes('INVALID_TOKEN') || errorStr.includes('TOKEN_EXPIRED')) {
      return 'Session expired. Please refresh and try starting the call again.';
    }

    // Return original message if no specific match
    return errorMessage || 'Failed to start call. Please try again.';
  }

  /**
   * Leave the current call
   */
  async leaveCall(): Promise<{ duration: number }> {
    const startTime = Date.now();

    try {
      // Close local tracks
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
        this.localAudioTrack = null;
      }

      if (this.localVideoTrack) {
        this.localVideoTrack.close();
        this.localVideoTrack = null;
      }

      // Leave the channel
      if (this.client) {
        await this.client.leave();
        this.client = null;
      }

      // Reset state
      this.state = {
        isConnected: false,
        isMuted: false,
        isCameraOff: false,
        isScreenSharing: false,
        remoteUser: null,
      };

      this.callbacks.onConnectionStateChange?.('disconnected');

      return { duration: Math.floor((Date.now() - startTime) / 1000) };

    } catch (error) {
      console.error('Failed to leave call:', error);
      throw error;
    }
  }

  /**
   * Toggle local audio mute state
   */
  async toggleMute(): Promise<boolean> {
    if (!this.localAudioTrack) {
      console.warn('No local audio track to mute');
      return this.state.isMuted;
    }

    this.state.isMuted = !this.state.isMuted;
    await this.localAudioTrack.setEnabled(!this.state.isMuted);

    return this.state.isMuted;
  }

  /**
   * Toggle local camera state
   */
  /**
   * Whether a camera track exists on this call.
   *
   * A video call falls back to audio only when no camera is available, and
   * then there is nothing for toggleCamera to act on. Callers need to be able
   * to say so rather than offering a control that does nothing.
   */
  hasLocalVideo(): boolean {
    return this.localVideoTrack !== null && this.localVideoTrack !== undefined;
  }

  async toggleCamera(containerId?: string): Promise<boolean> {
    if (!this.localVideoTrack) {
      console.warn('No local video track to toggle');
      return this.state.isCameraOff;
    }

    this.state.isCameraOff = !this.state.isCameraOff;
    await this.localVideoTrack.setEnabled(!this.state.isCameraOff);

    // If camera is being turned back on and container is specified, replay the video
    if (!this.state.isCameraOff && containerId) {
      // Small delay to ensure the container is in the DOM
      setTimeout(() => {
        if (this.localVideoTrack && !this.state.isCameraOff) {
          this.localVideoTrack.play(containerId);
        }
      }, 100);
    }

    return this.state.isCameraOff;
  }

  /**
   * Replay local video in a container (useful after toggling camera)
   */
  playLocalVideo(containerId: string): void {
    if (this.localVideoTrack && !this.state.isCameraOff) {
      this.localVideoTrack.play(containerId);
    }
  }

  /**
   * Start screen sharing
   */
  async startScreenShare(containerId?: string): Promise<void> {
    if (!this.client || this.state.isScreenSharing) return;

    try {
      // Stop camera track if it exists
      if (this.localVideoTrack) {
        await this.client.unpublish(this.localVideoTrack);
        this.localVideoTrack.close();
      }

      // Create screen share track
      const screenTrack = await AgoraRTC.createScreenVideoTrack({}, 'disable');

      // Handle single track or array (with audio)
      if (Array.isArray(screenTrack)) {
        this.localVideoTrack = screenTrack[0] as unknown as ICameraVideoTrack;
      } else {
        this.localVideoTrack = screenTrack as unknown as ICameraVideoTrack;
      }

      // Play in container if specified
      if (containerId && this.localVideoTrack) {
        this.localVideoTrack.play(containerId);
      }

      // Publish screen track
      await this.client.publish(this.localVideoTrack);

      this.state.isScreenSharing = true;

    } catch (error) {
      console.error('Failed to start screen share:', error);
      throw error;
    }
  }

  /**
   * Stop screen sharing and resume camera
   */
  async stopScreenShare(containerId?: string): Promise<void> {
    if (!this.client || !this.state.isScreenSharing) return;

    try {
      // Stop screen share track
      if (this.localVideoTrack) {
        await this.client.unpublish(this.localVideoTrack);
        this.localVideoTrack.close();
      }

      // Resume camera
      this.localVideoTrack = await AgoraRTC.createCameraVideoTrack();

      if (containerId && this.localVideoTrack) {
        this.localVideoTrack.play(containerId);
      }

      await this.client.publish(this.localVideoTrack);

      this.state.isScreenSharing = false;
      this.state.isCameraOff = false;

    } catch (error) {
      console.error('Failed to stop screen share:', error);
      throw error;
    }
  }

  /**
   * Get current call state
   */
  getState(): AgoraCallState {
    return { ...this.state };
  }

  /**
   * Set up event handlers for the Agora client
   */
  private setupEventHandlers(): void {
    if (!this.client) return;

    // User joined the channel
    this.client.on('user-joined', (user: IAgoraRTCRemoteUser) => {
      console.log('Remote user joined:', user.uid);
      this.callbacks.onUserJoined?.(user.uid);
    });

    // User left the channel
    this.client.on('user-left', (user: IAgoraRTCRemoteUser, reason: string) => {
      console.log('Remote user left:', user.uid, reason);
      this.state.remoteUser = null;
      this.remoteAudioTrack = null;
      this.remoteVideoTrack = null;
      this.callbacks.onUserLeft?.(user.uid, reason);
    });

    // User published audio/video
    this.client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      console.log('Remote user published:', user.uid, mediaType);

      // Subscribe to the remote user's track
      await this.client?.subscribe(user, mediaType);

      if (mediaType === 'audio') {
        this.remoteAudioTrack = user.audioTrack || null;
        // Play remote audio automatically
        this.remoteAudioTrack?.play();
      } else if (mediaType === 'video') {
        this.remoteVideoTrack = user.videoTrack || null;
        // Video playback is handled by the component
      }

      // Update remote user state
      this.state.remoteUser = {
        uid: user.uid,
        hasAudio: !!user.audioTrack,
        hasVideo: !!user.videoTrack,
      };

      this.callbacks.onUserPublished?.(user.uid, mediaType);
    });

    // User unpublished audio/video
    this.client.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      console.log('Remote user unpublished:', user.uid, mediaType);

      if (mediaType === 'audio') {
        this.remoteAudioTrack = null;
      } else if (mediaType === 'video') {
        this.remoteVideoTrack = null;
      }

      // Update remote user state
      if (this.state.remoteUser?.uid === user.uid) {
        this.state.remoteUser = {
          uid: user.uid,
          hasAudio: !!user.audioTrack,
          hasVideo: !!user.videoTrack,
        };
      }

      this.callbacks.onUserUnpublished?.(user.uid, mediaType);
    });

    // Connection state change
    this.client.on('connection-state-change', (curState, revState, reason) => {
      console.log('Connection state changed:', curState, 'from', revState, 'reason:', reason);

      if (curState === 'CONNECTED') {
        this.callbacks.onConnectionStateChange?.('connected');
      } else if (curState === 'DISCONNECTED' || curState === 'DISCONNECTING') {
        this.callbacks.onConnectionStateChange?.('disconnected');
      } else if (curState === 'RECONNECTING') {
        this.callbacks.onConnectionStateChange?.('connecting');
      }
    });

    // Exception handling
    this.client.on('exception', (event) => {
      console.error('Agora exception:', event);
    });
  }

  /**
   * Play remote video in a container element
   */
  playRemoteVideo(containerId: string): void {
    if (this.remoteVideoTrack) {
      this.remoteVideoTrack.play(containerId);
    }
  }

  /**
   * Check if camera/microphone permissions are available
   */
  static async checkDevicePermissions(): Promise<{
    audio: boolean;
    video: boolean;
  }> {
    try {
      const devices = await AgoraRTC.getDevices();
      const hasAudio = devices.some((d) => d.kind === 'audioinput');
      const hasVideo = devices.some((d) => d.kind === 'videoinput');

      return { audio: hasAudio, video: hasVideo };
    } catch {
      return { audio: false, video: false };
    }
  }

  /**
   * Get available audio/video devices
   */
  static async getDevices(): Promise<{
    audioInputs: MediaDeviceInfo[];
    videoInputs: MediaDeviceInfo[];
  }> {
    try {
      const devices = await AgoraRTC.getDevices();

      return {
        audioInputs: devices.filter((d) => d.kind === 'audioinput'),
        videoInputs: devices.filter((d) => d.kind === 'videoinput'),
      };
    } catch {
      return { audioInputs: [], videoInputs: [] };
    }
  }
}

// Export singleton instance
export const agoraService = new AgoraService();

export default agoraService;
